/**
 * Saúde da Monumenta Section
 *
 * High-level health indicators for the CEO dashboard:
 * - PROA Receita — Realizado vs PROA (circular)
 * - PROA Lucro — Realizado vs PROA (circular)
 * - Preenchimento Timesheet (circular)
 * - Margens (Bruta / Líquida)
 */

import { useMemo, useState } from 'react'
import { HelpCircle, Percent } from 'lucide-react'
import { getVarianceIconData, getVarianceColor } from '@/lib/financial-utils'
import type { FinancialData } from '@/components/financial/financial-config'
import { TimesheetFillRateCard } from '@/components/ceo/TimesheetFillRateCard'
import { TrendExplanationModal } from '@/components/ceo/TrendExplanationModal'
import { AIInsightModal } from '@/components/ai/AIInsightModal'
import { generateInsight } from '@/lib/services/ai-insights.service'
import { TREND_STATUS_CONFIG, type TrendResult } from '@/lib/services/trend-analysis.service'
import { HealthThermometer } from '@/components/ceo/HealthThermometer'

/**
 * % change between current and prev. Returns 0 if prev is missing/zero.
 * higherIsBetter=false inverts the sign for "lower is better" metrics (e.g. cost ratios).
 */
function periodVariance(
  current: number,
  prev: number | null | undefined,
  higherIsBetter = true
): number {
  if (prev === null || prev === undefined || prev === 0) return 0
  const raw = ((current - prev) / Math.abs(prev)) * 100
  return higherIsBetter ? raw : -raw
}

function VarianceBadge({ variance }: { variance: number }) {
  const { Icon } = getVarianceIconData(variance)
  return <Icon size={12} strokeWidth={1.5} className={getVarianceColor(variance)} />
}

interface SaudeMonumentaSectionProps {
  faturamento: FinancialData
  receitaBruta: FinancialData
  lucroLiquido: FinancialData
  /**
   * Optional adjusted Lucro Líquido value ("foto do presente" correction —
   * see EnhancedMetricCard). When provided, it is used in place of
   * `lucroLiquido.real` for:
   *   - PROA Lucro (current-period realization vs PROA)
   *   - Margens Bruta / Líquida (numerator)
   * Previous-period values are NOT adjusted — they remain raw historical
   * snapshots.
   */
  lucroLiquidoAjustado?: number
  /** Period start (ISO YYYY-MM-DD) used by the timesheet fill-rate chart */
  startDate: string
  /** Period end (ISO YYYY-MM-DD) used by the timesheet fill-rate chart */
  endDate: string
  /** Total timesheet hours filled in the period (currently mocked) */
  timesheetHorasPreenchidas: number
  /** Folha real YTD (absolute) — used by timesheet to show untracked payroll */
  folhaReal?: number
  /** Optional year-end projection trends */
  faturamentoTrend?: TrendResult | null
  receitaTrend?: TrendResult | null
  receitaFinanceiraTrend?: TrendResult | null
  folhaTrend?: TrendResult | null
  terceirosTrend?: TrendResult | null
  despesasTrend?: TrendResult | null
  impostosTrend?: TrendResult | null
  lucroLiquidoTrend?: TrendResult | null
}

// Shared SVG circle dimensions
const CIRCLE_SIZE = 160
const STROKE_WIDTH = 2
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

interface CircleProps {
  percent: number
  color: string
  children: React.ReactNode
}

function ProgressCircle({ percent, color, children }: CircleProps) {
  const clamped = Math.min(100, Math.max(0, percent))
  const offset = CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE

  return (
    <div className="relative flex-shrink-0" style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}>
      <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} className="-rotate-90">
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          stroke="#2A2D34"
          strokeWidth={STROKE_WIDTH}
          fill="none"
        />
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          fill="none"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  )
}

interface HealthCardShellProps {
  label: string
  visual: React.ReactNode
  details?: React.ReactNode
}

function HealthCardShell({ label, visual, details }: HealthCardShellProps) {
  return (
    <div className="flex items-center gap-4 py-3">
      <div className="min-w-0 flex-1">
        <span className="text-[11px] font-medium text-brand-gray uppercase tracking-wider block">
          {label}
        </span>
        {details && <div className="mt-1">{details}</div>}
      </div>
      {visual}
    </div>
  )
}

// ============================================================================
// Individual Cards
// ============================================================================

function ProaHealthCard({
  label,
  real,
  proa,
  trend,
  isAdjusted = false,
}: {
  label: string
  real: number
  proa: number
  trend?: TrendResult | null
  /** When true, renders a small "ajustado" marker under the label to signal
   *  the value includes the Lucro Ajustado correction. */
  isAdjusted?: boolean
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [insightModalOpen, setInsightModalOpen] = useState(false)
  const metricKey = label === 'Receita' ? 'receitaBruta' : 'lucroLiquido'
  const insight = useMemo(() => generateInsight(metricKey, label), [metricKey, label])
  const percent = proa > 0 ? (real / proa) * 100 : 0
  const over100 = percent >= 100
  const color = over100 ? '#D2FA52' : '#6B7280'
  const valueClass = over100 ? 'text-brand-primary' : 'text-white'

  const statusCfg = trend ? TREND_STATUS_CONFIG[trend.status] : null
  const projectedPct = trend
    ? Math.round(trend.projectedPercent * 100).toLocaleString('pt-BR')
    : null

  return (
    <div className="flex flex-col items-center justify-center py-3 gap-4">
      <ProgressCircle percent={percent} color={color}>
        <span className="text-[10px] sm:text-[11px] font-semibold text-brand-gray uppercase tracking-wider">
          {isAdjusted ? 'Lucro Ajustado' : label}
        </span>
        <span className={`text-3xl sm:text-4xl font-bold font-display ${valueClass}`}>
          {percent.toFixed(0)}%
        </span>
        <span className="text-[9px] sm:text-[10px] text-brand-gray uppercase tracking-wider mt-0.5">
          do PROA
        </span>
      </ProgressCircle>

      {trend && statusCfg && projectedPct !== null && (
        <>
          <div className="flex flex-col items-center gap-1.5">
            <button
              type="button"
              onClick={() => setInsightModalOpen(true)}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ${statusCfg.bg} ${statusCfg.border} hover:ring-1 hover:ring-white/50 transition-all cursor-pointer`}
              aria-label={`Ver insights de ${label}`}
            >
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className={`absolute inline-flex h-full w-full rounded-full ${statusCfg.dot} opacity-75 animate-ping`} />
                <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
              </span>
              <span className={`text-[11px] font-semibold ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
            </button>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-brand-gray text-center">
                Projeção{' '}
                <span className="font-bold text-white">Dez/{trend.currentYear}</span>
                {' '}= {projectedPct}% do PROA
              </span>
              <span className="text-brand-gray/40 text-[11px]">•</span>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="text-brand-gray hover:text-brand-primary transition-colors"
                aria-label={`Como a tendência de ${label} é calculada`}
              >
                <HelpCircle size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <TrendExplanationModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            metricLabel={label}
            trend={trend}
          />
          <AIInsightModal
            open={insightModalOpen}
            onOpenChange={setInsightModalOpen}
            insight={insight}
            onOpenCalculation={() => setModalOpen(true)}
          />
        </>
      )}
    </div>
  )
}

export function MargensHealthCard({
  faturamentoReal,
  faturamentoPrev,
  receitaBrutaReal,
  receitaBrutaPrev,
  lucroLiquidoReal,
  lucroLiquidoPrev,
  isAdjusted = false,
}: {
  faturamentoReal: number
  faturamentoPrev?: number
  receitaBrutaReal: number
  receitaBrutaPrev?: number
  lucroLiquidoReal: number
  lucroLiquidoPrev?: number
  /** When true, labels the numerator as "Lucro ajustado" to signal the
   *  value includes the Lucro Ajustado correction. */
  isAdjusted?: boolean
}) {
  const margemBruta = faturamentoReal > 0 ? (lucroLiquidoReal / faturamentoReal) * 100 : 0
  const margemLiquida = receitaBrutaReal > 0 ? (lucroLiquidoReal / receitaBrutaReal) * 100 : 0

  const prevMargemBruta =
    lucroLiquidoPrev !== undefined && faturamentoPrev !== undefined && faturamentoPrev > 0
      ? (lucroLiquidoPrev / faturamentoPrev) * 100
      : null
  const prevMargemLiquida =
    lucroLiquidoPrev !== undefined && receitaBrutaPrev !== undefined && receitaBrutaPrev > 0
      ? (lucroLiquidoPrev / receitaBrutaPrev) * 100
      : null

  const classFor = (v: number) => (v >= 0 ? 'text-brand-primary' : 'text-red-400')

  const lucroLabel = isAdjusted ? 'Lucro ajustado' : 'Lucro'
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
      <span className="text-[11px] font-semibold text-brand-gray uppercase tracking-wider flex items-center gap-1">
        <Percent size={12} strokeWidth={1.5} />
        {isAdjusted ? 'Margens Ajust.' : 'Margens'}
      </span>
      {/* Bruta */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Bruta</span>
        <span className="text-sm font-bold font-display text-white">
          {margemBruta.toFixed(1)}%
        </span>
        <span className="text-[9px] text-brand-gray-light flex items-center gap-0.5">
          ant: {prevMargemBruta !== null ? `${prevMargemBruta.toFixed(1)}%` : '—'}
          {prevMargemBruta !== null && (
            <VarianceBadge variance={periodVariance(margemBruta, prevMargemBruta)} />
          )}
        </span>
      </div>
      <span className="text-brand-dark-lighter text-xs">|</span>
      {/* Líquida */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Líquida</span>
        <span className="text-sm font-bold font-display text-white">
          {margemLiquida.toFixed(1)}%
        </span>
        <span className="text-[9px] text-brand-gray-light flex items-center gap-0.5">
          ant: {prevMargemLiquida !== null ? `${prevMargemLiquida.toFixed(1)}%` : '—'}
          {prevMargemLiquida !== null && (
            <VarianceBadge variance={periodVariance(margemLiquida, prevMargemLiquida)} />
          )}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Main Section
// ============================================================================

export function SaudeMonumentaSection({
  faturamento,
  receitaBruta,
  lucroLiquido,
  lucroLiquidoAjustado,
  startDate,
  endDate,
  timesheetHorasPreenchidas,
  folhaReal,
  faturamentoTrend,
  receitaTrend,
  receitaFinanceiraTrend,
  folhaTrend,
  terceirosTrend,
  despesasTrend,
  impostosTrend,
  lucroLiquidoTrend,
}: SaudeMonumentaSectionProps) {
  // Use adjusted value whenever available — otherwise fall back to the raw
  // DRE figure. All lucro-based calculations in this section (PROA Lucro,
  // Margens) honor the adjustment for consistency with the Lucro card.
  const lucroLiquidoRealEffective = lucroLiquidoAjustado ?? lucroLiquido.real
  const isLucroAdjusted = lucroLiquidoAjustado !== undefined
  return (
    <div className="space-y-4">
      {/* Top row — Thermometer + 3 circular progress cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-2 divide-y sm:divide-y-0 xl:divide-x divide-brand-dark-lighter">
        <div className="xl:pl-6">
          <HealthThermometer
            faturamentoTrend={faturamentoTrend}
            receitaTrend={receitaTrend}
            receitaFinanceiraTrend={receitaFinanceiraTrend}
            folhaTrend={folhaTrend}
            terceirosTrend={terceirosTrend}
            despesasTrend={despesasTrend}
            impostosTrend={impostosTrend}
            lucroTrend={lucroLiquidoTrend}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
        <div className="xl:pl-6">
          <ProaHealthCard
            label="Receita"
            real={receitaBruta.real}
            proa={receitaBruta.proa}
            trend={receitaTrend}
          />
        </div>
        <div className="xl:pl-6">
          <ProaHealthCard
            label="Lucro"
            real={lucroLiquidoRealEffective}
            proa={lucroLiquido.proa}
            trend={lucroLiquidoTrend}
            isAdjusted={isLucroAdjusted}
          />
        </div>
        <div className="xl:pl-6">
          <TimesheetFillRateCard
            startDate={startDate}
            endDate={endDate}
            horasPreenchidas={timesheetHorasPreenchidas}
            folhaReal={folhaReal}
          />
        </div>
      </div>
    </div>
  )
}
